/**
 * GD&T symbol glyphs (Faz 8.3): the 15+ gdt.lff characteristic symbols
 * drawn as line compositions (OCS renders them through the gdt.lff font;
 * yap draws geometric icons so no font dependency exists). Each glyph lives
 * in a unit square [0..1]² and renders through:
 *  - GdtSymbolIcon  — R3F/drei Lines (frame renderer in the viewport)
 *  - GdtSymbolSvg   — inline SVG (dialog grid buttons)
 */

import { Line, Text } from '@react-three/drei';
import * as THREE from 'three';
import type { GdtSymbol } from '../../core/commands/toleranceModel';

export interface GdtGlyph {
    /** Polylines in unit-square coordinates. */
    lines: number[][][];
    /** [cx, cy, r] circles. */
    circles?: number[][];
    /** Optional letter (material modifiers use text). */
    text?: string;
}

/** Dense arc polyline helper. */
function arc(cx: number, cy: number, r: number, a0: number, a1: number, n = 16): number[][] {
    const pts: number[][] = [];
    for (let i = 0; i <= n; i++) {
        const a = a0 + ((a1 - a0) * i) / n;
        pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
    }
    return pts;
}

function circlePts(cx: number, cy: number, r: number, n = 24): number[][] {
    return arc(cx, cy, r, 0, Math.PI * 2, n);
}

/** The symbol glyph table (gdt.lff code → drawing). */
export const GDT_GLYPHS: Record<string, GdtGlyph> = {
    // Straightness — one horizontal line.
    u: { lines: [[[0.12, 0.5], [0.88, 0.5]]] },
    // Flatness — parallelogram.
    c: { lines: [[[0.22, 0.2], [0.72, 0.2], [0.88, 0.8], [0.38, 0.8], [0.22, 0.2]]] },
    // Circularity — circle.
    e: { lines: [], circles: [[0.5, 0.5, 0.36]] },
    // Cylindricity — circle with two tangent slashes.
    g: {
        lines: [[[0.08, 0.12], [0.62, 0.94]], [[0.38, 0.06], [0.92, 0.88]]],
        circles: [[0.5, 0.5, 0.3]],
    },
    // Surface profile — arc resting on a baseline.
    d: {
        lines: [[[0.08, 0.2], [0.92, 0.2]], arc(0.5, 0.2, 0.34, 0, Math.PI)],
    },
    // Line profile — arc alone.
    k: { lines: [arc(0.5, 0.1, 0.4, 0.15, Math.PI - 0.15)] },
    // Position — circle with a cross.
    j: {
        lines: [[[0.5, 0.02], [0.5, 0.98]], [[0.02, 0.5], [0.98, 0.5]]],
        circles: [[0.5, 0.5, 0.28]],
    },
    // Concentricity — two concentric circles.
    r: { lines: [], circles: [[0.5, 0.5, 0.4], [0.5, 0.5, 0.18]] },
    // Symmetry — three bars (middle bar wider).
    i: {
        lines: [[[0.25, 0.18], [0.75, 0.18]], [[0.08, 0.5], [0.92, 0.5]], [[0.25, 0.82], [0.75, 0.82]]],
    },
    // Parallelism — two parallel slashes.
    f: { lines: [[[0.2, 0.1], [0.45, 0.9]], [[0.55, 0.1], [0.8, 0.9]]] },
    // Perpendicularity — inverted T.
    b: { lines: [[[0.12, 0.22], [0.88, 0.22]], [[0.5, 0.22], [0.5, 0.92]]] },
    // Angularity — angle.
    a: { lines: [[[0.12, 0.14], [0.9, 0.14]], [[0.12, 0.14], [0.58, 0.9]]] },
    // Circular runout — circle + leader arrow.
    h: {
        lines: [
            [[0.86, 0.1], [0.5, 0.52]],
            [[0.5, 0.52], [0.56, 0.38]], [[0.5, 0.52], [0.64, 0.5]],
        ],
        circles: [[0.42, 0.64, 0.24]],
    },
    // Total runout — baseline + two leader arrows.
    t: {
        lines: [
            [[0.08, 0.16], [0.92, 0.16]],
            [[0.32, 0.16], [0.56, 0.82]], [[0.56, 0.82], [0.47, 0.68]], [[0.56, 0.82], [0.66, 0.7]],
            [[0.68, 0.16], [0.86, 0.6]], [[0.86, 0.6], [0.77, 0.5]], [[0.86, 0.6], [0.9, 0.48]],
        ],
    },
    // Diameter — circle with a slash.
    n: {
        lines: [[[0.24, 0.82], [0.76, 0.18]]],
        circles: [[0.5, 0.5, 0.3]],
    },
    // Projected tolerance zone — box over a circle.
    p: {
        lines: [[[0.34, 0.58], [0.66, 0.58], [0.66, 0.94], [0.34, 0.94], [0.34, 0.58]]],
        circles: [[0.5, 0.34, 0.24]],
    },
    // Material modifiers render as letters.
    m: { lines: [], text: 'M' },
    l: { lines: [], text: 'L' },
    s: { lines: [], text: 'S' },
};

/** R3F icon: the glyph centered in a square of `size` world units. */
export function GdtSymbolIcon({ symbol, size, color, position }: {
    symbol: string;
    size: number;
    color: string;
    position: [number, number, number];
}) {
    const glyph = GDT_GLYPHS[symbol];
    if (!glyph) return null;
    const z = 0.005;
    return (
        <group position={[position[0], position[1], position[2]]}>
            {glyph.lines.map((pts, i) => (
                <Line
                    key={`l${i}`}
                    points={pts.map(([x, y]) => new THREE.Vector3(x * size, y * size, z))}
                    color={color}
                    lineWidth={1.2}
                />
            ))}
            {glyph.circles?.map(([cx, cy, r], i) => (
                <Line
                    key={`c${i}`}
                    points={circlePts(cx, cy, r).map(([x, y]) => new THREE.Vector3(x * size, y * size, z))}
                    color={color}
                    lineWidth={1.2}
                />
            ))}
            {glyph.text && (
                <Text
                    position={[0.5 * size, 0.5 * size, z]}
                    fontSize={size * 0.62}
                    color={color}
                    anchorX="center"
                    anchorY="middle"
                >
                    {glyph.text}
                </Text>
            )}
        </group>
    );
}

/** DOM icon: the same glyph as an inline SVG (dialog grid, 24px boxes). */
export function GdtSymbolSvg({ symbol, size = 24 }: { symbol: string; size?: number }) {
    const glyph = GDT_GLYPHS[symbol];
    if (!glyph) return null;
    const s = (v: number) => (v * size).toFixed(1);
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
            {glyph.lines.map((pts, i) => (
                <polyline
                    key={`l${i}`}
                    points={pts.map(([x, y]) => `${s(x)},${s(size - y)}`).join(' ')}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                />
            ))}
            {glyph.circles?.map(([cx, cy, r], i) => (
                <circle
                    key={`c${i}`}
                    cx={s(cx)}
                    cy={s(size - cy)}
                    r={s(r)}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                />
            ))}
            {glyph.text && (
                <text
                    x={s(0.5)}
                    y={s(0.68)}
                    textAnchor="middle"
                    fontSize={s(0.62)}
                    fill="currentColor"
                >
                    {glyph.text}
                </text>
            )}
        </svg>
    );
}
